import Button from '@/app/components/common/Button'
import CounterInput from '@/app/components/common/CounterInput'
import { Icons } from '@/app/components/ui/Icons'
import { Allocation } from '@/app/types'
import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { FC, useEffect, useRef, useState } from 'react'
import { CustomScroll } from 'react-custom-scroll'
import Select, {
	components,
	MenuListProps,
	SingleValue,
	SingleValueProps,
} from 'react-select'
import {
	emitAddRoomClickGAEvent,
	emitSaveGuestsClickGAEvent,
} from '../ga-event-helpers'

type OptionType = {
	value: number
	label: string
}

interface GuestsSelectProps {
	initialValue: Allocation[]
	onChange: (value: Allocation[]) => void
	onClose: () => void
}

const MAX_ADULTS_PER_ROOM = 4
const MAX_CHILDREN_PER_ROOM = 4
const MAX_ROOMS = 8
const MIN_ADULTS = 1
const MAX_COUNT_PER_ROOM = 4

const GuestsSelect: FC<GuestsSelectProps> = ({
	onChange,
	onClose,
	initialValue,
}) => {
	const scrollRef = useRef<HTMLDivElement>(null)
	const t = useTranslations('Form')
	const [rooms, setRooms] = useState<Allocation[]>(initialValue)

	useEffect(() => {
		onChange(rooms)
	}, [rooms, onChange])

	const NUMBER_OPTIONS_CHILDREN = new Array(18).fill(0).map((_, i) => ({
		value: i,
		label: i === 0 ? '<1' : String(i),
	}))

	const updateRoom = (index: number, updatedRoom: Partial<Allocation>) => {
		const newRooms = rooms.map((room, i) =>
			i === index ? { ...room, ...updatedRoom } : room
		)
		setRooms(newRooms)
	}

	const handleAdultsChange = (index: number, count: number) => {
		if (count >= MIN_ADULTS && count <= MAX_ADULTS_PER_ROOM) {
			const currentRoom = rooms[index]
			const totalGuests = count + currentRoom.childAge.length

			if (totalGuests <= MAX_COUNT_PER_ROOM) {
				updateRoom(index, { adults: count })
			}
		}
	}

	const handleChildrenChange = (index: number, count: number) => {
		if (count >= 0 && count <= MAX_CHILDREN_PER_ROOM) {
			const currentRoom = rooms[index]
			const totalGuests = currentRoom.adults + count

			if (totalGuests <= MAX_COUNT_PER_ROOM) {
				const currentChildCount = currentRoom.childAge.length
				let newChildAge = [...currentRoom.childAge]

				if (count > currentChildCount) {
					newChildAge = [
						...newChildAge,
						...Array(count - currentChildCount).fill(0),
					]
				} else if (count < currentChildCount) {
					newChildAge = newChildAge.slice(0, count)
				}

				updateRoom(index, { childAge: newChildAge })
			}
		}
	}

	const handleChildAgeChange = (
		roomIndex: number,
		childIndex: number,
		age: number
	) => {
		const newRooms = [...rooms]
		newRooms[roomIndex].childAge[childIndex] = age
		setRooms(newRooms)
	}

	const addRoom = () => {
		if (rooms.length < MAX_ROOMS) {
			setRooms([...rooms, { adults: 1, childAge: [] }])
		}

		emitAddRoomClickGAEvent()

		setTimeout(() => {
			scrollRef.current?.scrollTo({ top: Number.MAX_SAFE_INTEGER })
		}, 100)
	}

	const removeRoom = (index: number) => {
		setRooms(rooms.filter((_, i) => i !== index))
	}

	const CustomMenuList = (props: MenuListProps<OptionType, false>) => {
		const { children } = props

		return (
			<CustomScroll>
				<div className="max-h-[220px] rounded-t-none">{children}</div>
			</CustomScroll>
		)
	}

	const CustomSingleValue = (props: SingleValueProps<OptionType>) => {
		return (
			<components.SingleValue {...props}>
				<span className="text-sm">{props.data.label}</span>
			</components.SingleValue>
		)
	}

	const IndicatorsContainer = () => {
		return (
			<div className="flex items-center pr-3">
				<Icons.caret className="h-4 w-4 text-gray-500" />
			</div>
		)
	}

	const onSaveClick = () => {
		emitSaveGuestsClickGAEvent()
		onClose()
	}

	return (
		<div className="guests-select">
			<div ref={scrollRef} className="guests-select__rooms">
				{rooms.map((room, roomIndex) => {
					const shouldDisableIncrement =
						room.adults + room.childAge.length >= MAX_COUNT_PER_ROOM
					return (
						<div key={roomIndex} className="room-container">
							<div className="flex justify-between max-md:flex-col">
								<div className="room-header">
									<div>
										{rooms.length > 1 && (
											<button
												onClick={() => removeRoom(roomIndex)}
												className="lg:me-3 flex items-center"
											>
												<span className="lg:hidden text-score-good me-1 font-semibold text-sm">
													{t('remove')}
												</span>
												<Icons.iconClose className="w-5 h-5" stroke="#003E74" />
											</button>
										)}
									</div>

									<h3 className="font-bold">
										{t('room')} {roomIndex + 1}
									</h3>
									<div className="absolute  lg:hidden w-[calc(100%+24px)] -left-3 -bottom-3 bg-text-300 h-[1px]" />
								</div>
								<div className="grow room-content mt-4">
									<div className="flex justify-between w-full">
										<CounterInput
											min={MIN_ADULTS}
											max={MAX_ADULTS_PER_ROOM}
											label={t('adults')}
											count={room.adults}
											onCountChange={(count) =>
												handleAdultsChange(roomIndex, count)
											}
											disableIncrement={shouldDisableIncrement}
										/>
										<CounterInput
											min={0}
											max={MAX_CHILDREN_PER_ROOM}
											label={t('children')}
											count={room.childAge.length}
											isChildInput
											onCountChange={(count) =>
												handleChildrenChange(roomIndex, count)
											}
											disableIncrement={shouldDisableIncrement}
										/>
									</div>
									{room.childAge.length > 0 && (
										<div className="child-ages-container">
											{room.childAge.map((age, childIndex) => (
												<div
													key={childIndex}
													className={classNames(
														'flex items-center justify-between space-x-2',
														{
															'mt-2': childIndex > 0,
														}
													)}
												>
													<span>
														{t('ageOfChild')} {childIndex + 1}*:
													</span>
													<Select<OptionType>
														isSearchable={false}
														className="w-[88px] lg:w-26 rounded-lg"
														classNames={{
															control: (props) => {
																return `h-11 ![direction:ltr] !rounded-lg ${
																	props.isFocused ? '!rounded-b-none' : ''
																} !shadow-none !border-text-200`
															},
															indicatorSeparator: () => 'hidden',
															menu: () =>
																'z-[100] !border-text-200 !rounded-t-none !shadow-none border border-text-200',
															option: (props) => {
																return `
														px-3 py-2 border-b border-text-200 cursor-pointer
														${
															props.isSelected
																? '!bg-text-100 !text-black transition-all'
																: 'hover:bg-text-200 transition-colors'
														}
														${props.isFocused && !props.isSelected ? 'bg-text-100' : ''}
													`
															},
														}}
														menuPortalTarget={
															typeof document !== 'undefined'
																? document.body
																: undefined
														}
														menuPosition="fixed"
														styles={{
															menuPortal: (provided) => {
																return { ...provided, zIndex: 9999 } as any
															},
															menu: (provided) => {
																return {
																	...provided,
																	marginTop: '-1px',
																	borderColor: '#E2E8F0',
																} as any
															},
														}}
														options={NUMBER_OPTIONS_CHILDREN}
														value={NUMBER_OPTIONS_CHILDREN.find(
															(option) => option.value === age
														)}
														onChange={(option: SingleValue<OptionType>) => {
															if (option)
																handleChildAgeChange(
																	roomIndex,
																	childIndex,
																	option.value
																)
														}}
														placeholder={'Age'}
														components={{
															IndicatorsContainer: IndicatorsContainer as any,
															MenuList: CustomMenuList,
															SingleValue: CustomSingleValue,
														}}
													/>
												</div>
											))}
										</div>
									)}
								</div>
							</div>
						</div>
					)
				})}
			</div>
			<div className="max-lg:px-0 p-4 flex justify-between">
				<Button
					onClick={addRoom}
					className={classNames(
						'!rounded-full text-score-good bg-text-200 w-fit font-semibold text-sm flex items-center',
						{ 'opacity-0 pointer-events-none': rooms.length >= MAX_ROOMS }
					)}
					name={t('addRoom')}
					id="add-room-button"
				>
					{t('addRoom')}{' '}
					<Icons.plus stroke="#003E74" className="w-5 h-5 ms-2" />
				</Button>
				<Button
					onClick={onSaveClick}
					id="save-rooms-and-guests-button"
					className="hidden md:block !rounded-full w-32 text-white bg-dark-blue font-semibold text-sm"
					name={t('save')}
				>
					{t('save')}{' '}
				</Button>
			</div>
			<div
				className={classNames('guests-select__disclaimer ', {
					'h-9 py-2': rooms.some(
						(room) => room.adults + room.childAge.length === MAX_COUNT_PER_ROOM
					),
				})}
			>
				{t('guestCountDisclaimer')}
			</div>
		</div>
	)
}

export default GuestsSelect
